import { useTranslate } from '@tolgee/react';

export type ValuationLevel = 'short' | 'prep' | 'final';

const LEVELS: Record<
  ValuationLevel,
  { cl: string; i: number; full: string; short: string; key?: string }
> = {
  short: { cl: 'c-neu', i: 1, full: 'Kurzbewertung', short: 'Kurzbewertung' },
  prep: {
    cl: 'c-info',
    i: 2,
    full: 'Detailbewertung in Vorbereitung',
    short: 'In Vorbereitung',
  },
  final: {
    cl: 'c-ok',
    i: 3,
    full: 'Detailbewertung (IAZI)',
    short: 'Detailbewertung (IAZI)',
    key: 'level_final_label',
  },
};

export function LevelBadge({ level }: { readonly level: ValuationLevel }) {
  const { t } = useTranslate();
  const l = LEVELS[level];
  const full = t(l.key ?? `level_${level}_full`, l.full);
  const short = t(l.key ?? `level_${level}_short`, l.short);
  return (
    <span
      className="lvw"
      data-level={level}
    >
      <span
        className={`chip lvb ${l.cl}`}
        role="img"
        aria-label={full}
        title={full}
        data-od-id="level-badge"
        data-level={level}
      >
        <span
          className="lvs"
          aria-hidden="true"
        >
          {[
            1,
            2,
            3,
          ].map((i) => (
            <i
              key={i}
              className={i <= l.i ? 'on' : undefined}
            />
          ))}
        </span>
        <span
          className="lvf"
          aria-hidden="true"
        >
          {full}
        </span>
        {short !== full && (
          <span
            className="lvk"
            aria-hidden="true"
          >
            {short}
          </span>
        )}
      </span>
    </span>
  );
}

/** Progress strip of the 8 owner details that upgrade Kurzbewertung to Detailbewertung. */
export function LevelProgress({
  filled,
  total,
  level,
}: {
  readonly filled: number;
  readonly total: number;
  /** Overrides the level derived from `filled`, e.g. 'final' once the IAZI valuation exists. */
  readonly level?: ValuationLevel;
}) {
  const { t } = useTranslate();
  const left = total - filled;
  const text =
    filled === 0
      ? t(
          'level_progress_none',
          'Noch keine Detailangaben. Sie sehen eine Kurzbewertung.',
        )
      : left > 0
      ? left === 1
        ? t(
            'level_progress_left_one',
            'Noch {count} Angabe bis zur vollständigen Vorbereitung. Danach holt Ihr Berater die IAZI-Bewertung ein.',
            { count: left },
          )
        : t(
            'level_progress_left_other',
            'Noch {count} Angaben bis zur vollständigen Vorbereitung. Danach holt Ihr Berater die IAZI-Bewertung ein.',
            { count: left },
          )
      : t(
          'level_progress_all',
          'Alle Angaben erfasst. Nach dem Speichern prüft Ihr Berater und holt die IAZI-Bewertung ein.',
        );
  return (
    <section
      className="card lvp"
      data-od-id="level-progress"
      aria-label={t('level_progress_label', 'Fortschritt Detailbewertung')}
    >
      <div className="row wrap">
        <div style={{ flex: '1 1 150px', minWidth: 0 }}>
          <LevelBadge level={level ?? (filled === 0 ? 'short' : 'prep')} />
        </div>
        <b className="sm num">
          {filled} {t('level_progress_of', 'von')} {total}{' '}
          {t('level_progress_details', 'Detailangaben')}
        </b>
      </div>
      <div
        className={`pbar${left === 0 ? ' fin' : ''}`}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={filled}
        aria-label={t(
          'level_progress_bar_label',
          'Angaben für die Detailbewertung',
        )}
      >
        <i style={{ width: `${(filled / total) * 100}%` }} />
      </div>
      <p
        className="xs mu"
        aria-live="polite"
      >
        {text}
      </p>
    </section>
  );
}
